/**
 * Détection, persistance et store de la langue courante.
 *
 * Comportement fidèle au prototype (`docs/legacy-prototype/nestor_prod.html`
 * l. 719-725) :
 *  1. localStorage `nestor_lang` (s'il existe et est supporté).
 *  2. `navigator.language` tronqué aux 2 premiers caractères, lowercase.
 *  3. `'fr'` par défaut.
 *
 * Le store est piloté par `lib/store.ts` (cf. ADR 0005). Toute UI peut
 * s'abonner via `subscribeLang(listener)` et déclencher son re-render
 * lit-html quand la langue change.
 */
import { createStore } from '../lib/store.js';
import {
  DEFAULT_LANG,
  SUPPORTED_LANGS,
  getDictionary,
  loadDictionary,
  type Lang,
} from './strings.js';

const STORAGE_KEY = 'nestor_lang';

interface LangState {
  lang: Lang;
  /**
   * Compteur de « révision » (Phase 8C) : incrémenté quand le
   * dictionnaire d'une langue lazy vient d'arriver, pour re-notifier
   * les abonnés `subscribeLang` (le store ne notifie pas si `lang`
   * est inchangé) et re-rendre l'UI avec les vraies traductions.
   */
  rev: number;
}

function isSupported(value: unknown): value is Lang {
  return typeof value === 'string' && (SUPPORTED_LANGS as readonly string[]).includes(value);
}

/**
 * Lit la langue persistée si présente et supportée.
 *
 * Accepte un `Storage` injecté pour faciliter les tests ; par défaut
 * cible `globalThis.localStorage` quand il existe (cas SSR / Node sans
 * jsdom : retourne `null` proprement).
 */
export function readStoredLang(storage: Storage | undefined = safeLocalStorage()): Lang | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    return isSupported(raw) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Détecte la langue à utiliser. Priorité : storage → navigator → défaut.
 *
 * `navigator` peut être absent (Node) ou ne pas exposer `language`. Tout
 * `try/catch` est volontaire : la détection ne doit jamais lever, fallback
 * propre sur la langue par défaut.
 */
export function detectLang(
  storage: Storage | undefined = safeLocalStorage(),
  nav: { language?: string } | undefined = safeNavigator(),
): Lang {
  const stored = readStoredLang(storage);
  if (stored) return stored;

  const raw = nav?.language ?? '';
  const device = raw.slice(0, 2).toLowerCase();
  return isSupported(device) ? device : DEFAULT_LANG;
}

const langStore = createStore<LangState>({ lang: detectLang(), rev: 0 });

/**
 * Synchronise `<html lang>` avec la langue courante (Phase 8,
 * accessibilité) : les lecteurs d'écran choisissent leur voix et la
 * prononciation d'après cet attribut. No-op hors DOM (tests node).
 */
function syncDocumentLang(lang: Lang): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = lang;
}

// Aligne l'attribut au boot (l'index.html est figé sur lang="fr").
syncDocumentLang(langStore.getState().lang);

/**
 * Garantit que le dictionnaire de `lang` est disponible (Phase 8C).
 * Fire-and-forget : à l'arrivée d'un chunk lazy, on incrémente `rev`
 * pour re-notifier les vues. En cas d'échec (réseau), on reste sur le
 * fallback FR — l'app demeure utilisable (CLAUDE.md §2.4).
 */
function ensureDictionary(lang: Lang): void {
  if (getDictionary(lang)) return;
  loadDictionary(lang)
    .then(() => {
      langStore.setState((s) => ({ rev: s.rev + 1 }));
    })
    .catch(() => {
      // Chunk inaccessible : fallback FR silencieux.
    });
}

// La langue détectée au boot peut être lazy (choix persisté) — on la
// charge immédiatement.
ensureDictionary(langStore.getState().lang);

/** Renvoie la langue courante. */
export function getLang(): Lang {
  return langStore.getState().lang;
}

/**
 * Change la langue courante et persiste le choix dans `localStorage`.
 * Notifie les listeners abonnés via `subscribeLang`.
 *
 * Si la langue passée n'est pas supportée, l'appel est ignoré (no-op
 * silencieux — on évite de casser l'UI sur une mauvaise valeur).
 */
export function setLang(next: Lang): void {
  if (!isSupported(next)) return;
  if (next === getLang()) return;

  langStore.setState({ lang: next });
  syncDocumentLang(next);
  ensureDictionary(next);

  const storage = safeLocalStorage();
  if (storage) {
    try {
      storage.setItem(STORAGE_KEY, next);
    } catch {
      // localStorage indisponible ou plein — on continue sans persister.
      // Le choix tient pour la session courante.
    }
  }
}

/** Abonne un listener aux changements de langue. Retourne unsubscribe. */
export function subscribeLang(listener: (lang: Lang) => void): () => void {
  return langStore.subscribe((state) => {
    listener(state.lang);
  });
}

/**
 * Helper de test : remet l'état du store à une valeur déterministe. Ne
 * touche pas au storage (le test gère son propre nettoyage).
 *
 * Si `forced` est passé, l'état est mis à cette langue (déterministe quel
 * que soit l'environnement). Sinon, on relance la détection — utile pour
 * vérifier le comportement de détection lui-même.
 *
 * @internal
 */
export function _resetLangForTests(forced?: Lang): void {
  langStore.setState({ lang: forced ?? detectLang() }, true);
}

function safeLocalStorage(): Storage | undefined {
  try {
    return typeof globalThis !== 'undefined' && 'localStorage' in globalThis
      ? globalThis.localStorage
      : undefined;
  } catch {
    return undefined;
  }
}

function safeNavigator(): { language?: string } | undefined {
  try {
    return typeof globalThis !== 'undefined' && 'navigator' in globalThis
      ? (globalThis as unknown as { navigator: { language?: string } }).navigator
      : undefined;
  } catch {
    return undefined;
  }
}
